<template>
  <BaseDialog
    v-if="open"
    :open="open"
    :max-width="dialogMaxWidth"
    @close="$emit('close')"
  >
    <template #title>
      Tagesbericht — <strong>{{ studentName || 'Schüler' }}</strong>
      <span class="muted"> ({{ dateDisplay }})</span>
    </template>

    <div class="timeline">
      <div
        v-if="timelineRows.length === 0"
        class="muted"
      >
        Keine Aktivitäten vorhanden.
      </div>

      <!-- Akzeptierte Meldung: spezielle Warn-Zeile -->
      <div
        v-if="hasAcceptedNotification"
        class="tl-row-rail warn-row"
      >
        <div class="tl-left">
          <em class="pl-2"> Meldung für diesen Tag vorhanden</em>
        </div>
        <div
          class="tl-line"
          aria-hidden="true"
        />
        <div class="tl-right">
          <span
            class="accept-text"
            :title="acceptedNotificationSubject"
          >
            {{ truncate(acceptedNotificationSubject, 70) }}
          </span>
        </div>
      </div>

      <!-- NEU: Ganztägige (mehrtägige) Angebote als Info abgesetzt -->
      <div
        v-if="allDayOffers.length > 0"
        class="notice notice mt-12"
        role="status"
      >
        <div
          class="muted"
          style="font-size: 12px; line-height: 16px; margin-bottom: 6px;"
        >
          Ganztägige Angebote (werden nicht in die Timeline eingezeichnet):
        </div>
        <ul style="margin: 0; padding-left: 16px;">
          <li
            v-for="(o, i) in allDayOffers"
            :key="i"
          >
            <strong>{{ o.subject }}</strong>
            <span class="muted"> ({{ o.rangeText }})</span>
          </li>
        </ul>
      </div>

      <div
        v-for="(r, idx) in timelineRows"
        :key="idx"
        class="tl-row-rail"
      >
        <div class="tl-left">
          <template v-if="r.kind === 'arrival'">
            <em v-if="r.first">Ankunft</em>
            <em v-else>Wiederkehr</em>
          </template>
          <template v-else-if="r.kind === 'release'">
            <em>Entlassen</em><em
              v-if="r.reason"
              class="muted"
            > ({{ r.reason }})</em>
            <span
              v-if="r.note"
              class="release-note"
            >{{ r.note }}</span>
          </template>
          <template v-else>
            <strong :class="{ underline: r.isMandatory }">{{ r.subject || r.leftLabel }}</strong>
            <span
              v-if="r.startTxt || r.endTxt"
              class="muted"
            >
              ({{ r.startTxt }}<span v-if="r.endTxt"> - {{ r.endTxt }}</span>)
            </span>
          </template>
        </div>
        <div class="tl-line">
          <template v-if="r.kind === 'arrival'">
            <span
              class="tl-caret caret-in"
              aria-hidden="true"
            />
          </template>
          <template v-else-if="r.kind === 'release'">
            <span
              class="tl-caret caret-out"
              aria-hidden="true"
            />
          </template>
          <template v-else>
            <span
              class="tl-dot"
              :class="r.dotClass"
              aria-hidden="true"
            />
          </template>
          <span
            class="tl-rail"
            aria-hidden="true"
          />
        </div>
        <div class="tl-right">
          <template v-if="r.kind === 'arrival'">
            <span>{{ r.timeText }}</span>
            <i
              v-if="r.externalUploaded"
              class="pi pi-cloud-upload upload-icon"
              aria-hidden="true"
              title="Extern übermittelt"
            />
            <button
              v-if="r.canEditStay"
              type="button"
              class="btn icon-btn"
              :aria-label="r.first ? 'Aufenthalt ab Ankunft bearbeiten' : 'Aufenthalt bearbeiten'"
              @click="onEditStayClick(r)"
            >
              <svg
                class="icon-edit"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4 17.25V20h2.75L17.81 8.94l-2.75-2.75L4 17.25z"
                  fill="currentColor"
                />
                <path
                  d="M19.71 7.04a1 1 0 0 0 0-1.41L17.37 3.29a1 1 0 0 0-1.41 0l-1.34 1.34 2.75 2.75 1.34-1.34z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </template>
          <template v-else-if="r.kind === 'release'">
            <span>{{ r.timeText }}</span>
            <i
              v-if="r.externalUploaded"
              class="pi pi-cloud-upload upload-icon"
              aria-hidden="true"
              title="Extern übermittelt"
            />
            <button
              v-if="r.canEditStay"
              type="button"
              class="btn icon-btn"
              aria-label="Aufenthalt bis Entlassung bearbeiten"
              @click="onEditStayClick(r)"
            >
              <svg
                class="icon-edit"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4 17.25V20h2.75L17.81 8.94l-2.75-2.75L4 17.25z"
                  fill="currentColor"
                />
                <path
                  d="M19.71 7.04a1 1 0 0 0 0-1.41L17.37 3.29a1 1 0 0 0-1.41 0l-1.34 1.34 2.75 2.75 1.34-1.34z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </template>
          <template v-else>
            <template v-if="r.hasAnyTime">
              Kommen: {{ r.kommenText || '-' }}, Gehen: {{ r.gehenText || '-' }}
              <span
                v-if="r.isMandatory && r.fehlzeit"
                :class="r.lateClass"
              >{{ r.fehlzeit }}</span>
            </template>
            <template v-else>
              - <span
                v-if="r.isMandatory && r.fehlzeit"
                :class="r.lateClass"
              >{{ r.fehlzeit }}</span>
            </template>
          </template>
        </div>
      </div>

      <div
        v-if="totalLateHHMM"
        class="tl-summary"
      >
        <span class="muted">Gesamt Fehlzeit (Pflichtangebote):</span>
        <span class="late-total">{{ totalLateHHMM }}</span>
      </div>
    </div>

    <!-- Dialog zum Bearbeiten eines Aufenthalts (Stay) -->
    <StudentStayEditDialog
      :open="stayEditState.open"
      :stay="stayEditState.stay"
      :date="date"
      @close="stayEditState.open = false"
      @saved="handleStaySaved"
    />

    <!-- NEU: Sicherheitsdialog -->
    <BaseDialog
      v-if="confirmResetOpen"
      :open="confirmResetOpen"
      :max-width="'560px'"
      :disabled="resetBusy"
      @close="confirmResetOpen = false"
    >
      <template #title>
        Tagesdaten wirklich entfernen?
      </template>

      <p class="muted">
        Es werden für <strong>{{ studentName || 'diesen Schüler' }}</strong> am <strong>{{ dateDisplay }}</strong>
        folgende Einträge zurückgesetzt:
      </p>
      <ul class="muted">
        <li><strong>Schulaufenthalte</strong> (Ankunft/Entlassung) dieses Tages werden gelöscht.</li>
        <li><strong>Angebotszeiten</strong> (Kommen/Gehen) dieses Tages werden zurückgesetzt.</li>
      </ul>
      <p class="muted">
        Meldungen, Angebote und Hinweise bleiben unverändert. Die Auswertung (z.B. Fehltage) kann sich ändern.
      </p>

      <template #footer>
        <div class="dialog-footer">
          <button
            class="btn"
            type="button"
            :disabled="resetBusy"
            @click="confirmResetOpen = false"
          >
            Abbrechen
          </button>
          <button
            class="btn btn-danger"
            type="button"
            :disabled="resetBusy"
            @click="doResetDailyPresence"
          >
            <span
              v-if="resetBusy"
              class="spinner"
              aria-hidden="true"
            />
            Ja, zurücksetzen
          </button>
        </div>
      </template>
    </BaseDialog>

    <template #footer>
      <div class="dialog-footer">
        <button
          class="btn"
          type="button"
          @click="$emit('close')"
        >
          Schließen
        </button>

        <button
          class="btn btn-danger"
          type="button"
          :disabled="resetBusy"
          title="Alle Stays löschen und Angebotszeiten (Kommen/Gehen) zurücksetzen"
          @click="confirmResetOpen = true"
        >
          Tag zurücksetzen
        </button>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {useToast as usePrimeToast} from 'primevue/usetoast';
import BaseDialog from '@/components/BaseDialog.vue';
import StudentStayEditDialog from './StudentStayEditDialog.vue';
import {reportsApi} from '@/api/reports';
import {eventsApi} from '@/api/events';

const toast = usePrimeToast();
const confirmResetOpen = ref(false);
const resetBusy = ref(false);

const props = defineProps({
  open: {type: Boolean, default: false},
  studentName: {type: String, default: ''},
  date: {type: String, default: ''},
  // events werden nicht mehr benötigt; bleiben optional für Backcompat
  events: {type: Array, default: () => []},
  studentId: {type: [Number, String], default: null},
  graceMinutes: {type: Number, default: 5},
  // Wenn vorhanden: Betreff einer akzeptierten Meldung für diesen Tag
  acceptedNotificationSubject: {type: String, default: ''}
});
const emit = defineEmits(['close', 'saved']);

const hasAcceptedNotification = computed(() => {
  const s = String(props.acceptedNotificationSubject || '').trim();
  return s.length > 0;
});

// Backend-Report (komplette Timeline)
const reportData = ref({timeline: [], totalLateSeconds: 0, allDayOffers: []});

// Lokaler State für Stay-Bearbeitung
const stayEditState = ref({
  open: false,
  stay: null
});

watch(() => [props.open, props.studentId, props.date], () => {
  if (props.open) loadDailyReport();
});

// optional: wenn Dialog geschlossen wird, Confirm/Busy sauber zurücksetzen
watch(() => props.open, (isOpen) => {
  if (!isOpen) {
    confirmResetOpen.value = false;
    resetBusy.value = false;
  }
});

async function doResetDailyPresence() {
  const sid = Number(props.studentId);
  if (!Number.isFinite(sid) || sid <= 0 || !props.date) return;

  resetBusy.value = true;
  try {
    await reportsApi.deleteStudentDailyPresence({studentId: sid, date: props.date});

    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});

    confirmResetOpen.value = false;

    await loadDailyReport();
    try {
      await eventsApi.getDayGrouped({date: props.date, groupBy: 'student'});
    } catch (e) {
      // still ignorieren
    }
    emit('saved');
  } catch (e) {
    toast.add({severity: 'error', summary: 'Löschen fehlgeschlagen.', life: 3500});
  } finally {
    resetBusy.value = false;
  }
}

async function loadDailyReport() {
  const sid = Number(props.studentId);
  if (!props.open || !Number.isFinite(sid) || sid <= 0 || !props.date) {
    reportData.value = {timeline: [], totalLateSeconds: 0, allDayOffers: []};
    return;
  }
  try {
    const res = await reportsApi.getStudentDaily({studentId: sid, date: props.date});
    const d = res?.data || {};
    reportData.value = {
      timeline: Array.isArray(d.timeline) ? d.timeline : [],
      totalLateSeconds: Number(d.totalLateSeconds || 0),
      allDayOffers: Array.isArray(d.allDayOffers) ? d.allDayOffers : []
    };
  } catch (e) {
    reportData.value = {timeline: [], totalLateSeconds: 0, allDayOffers: []};
  }
}

watch(() => [props.open, props.studentId, props.date], () => {
  if (props.open) loadDailyReport();
});

// Kürzt Text nach max Zeichen und hängt '...' an (wenn nötig)
function truncate(txt, max = 120) {
  if (!txt && txt !== 0) return '';
  const s = String(txt);
  return s.length > max ? s.slice(0, max) + '...' : s;
}

function secondsToHHMM(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '';
  const mins = Math.floor(n / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatHHMM(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {hour: '2-digit', minute: '2-digit'}).format(d);
}

// NEU: Berlin-Helpers für robuste Tagesvergleiche (unabhängig von Browser-Zeitzone)
function berlinParts(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);

  const map = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const y = map.year;
  const m = map.month;
  const d = map.day;
  const hh = map.hour;
  const mm = map.minute;
  if (!y || !m || !d || !hh || !mm) return null;

  return {
    ymd: `${y}-${m}-${d}`,
    minutes: Number(hh) * 60 + Number(mm)
  };
}

function berlinYmd(date) {
  return berlinParts(date)?.ymd || '';
}

// NEU: Regel „Ganztägiges Angebot für props.date“
// - Angebot überdeckt den Tag vollständig (00:00..23:59 Berlin)
// - UND es startet NICHT an dem Tag und endet NICHT an dem Tag (sonst soll es in die Timeline)
function isAllDayOfferForReportDay(startDate, endDate) {
  const day = String(props.date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;

  const sYmd = berlinYmd(startDate);
  const eYmd = berlinYmd(endDate);
  if (!sYmd || !eYmd) return false;

  // muss den Tag überlappen
  if (day < sYmd || day > eYmd) return false;

  // Randtage sollen in die Timeline
  if (sYmd === day || eYmd === day) return false;

  // Zwischentag => per Definition ganztägig (im Tagesbericht als Info absetzen)
  return true;
}

// NEU: Format abhängig davon, ob Datum sichtbar sein muss
function formatTimeForAttendance(d, showDate) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return showDate ? dmyHiBerlinFromDate(d) : formatHHMM(d);
}

// NEU: erkennt „mehrtägiges Angebot“ in Berlin (nicht Browser-TZ!)
function isMultiDayOfferBerlin(start, end) {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return false;
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return false;
  const sYmd = berlinYmd(start);
  const eYmd = berlinYmd(end);
  return !!sYmd && !!eYmd && sYmd !== eYmd;
}

// NEU: Breite richtet sich nach dem, was tatsächlich in der Timeline landet
const dialogMaxWidth = computed(() => {
  // Regel 1: sobald der abgesetzte Bereich sichtbar ist => klein
  if (Array.isArray(allDayOffers.value) && allDayOffers.value.length > 0) {
    return '640px';
  }

  // Regel 2: groß nur, wenn ein Multi-Day-Event in timelineRows enthalten ist
  const tl = Array.isArray(reportData.value?.timeline) ? reportData.value.timeline : [];

  for (const it of tl) {
    if (String(it?.kind || '') !== 'event') continue;

    const start = it?.starts_at ? new Date(it.starts_at) : null;
    const end = it?.ends_at ? new Date(it.ends_at) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;

    // exakt dieselbe Ausschlussregel wie für timelineRows:
    // ganztägige Zwischentage werden NICHT in die Timeline gezeichnet
    if (isAllDayOfferForReportDay(start, end)) continue;

    // wenn es dann mehrtägig ist => Datum wird in Timeline sichtbar => Platzbedarf hoch
    if (isMultiDayOfferBerlin(start, end)) {
      return '800px';
    }
  }

  return '640px';
});

function dmyHiBerlinFromDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

// NEU: Info-Liste für ganztägige (Zwischentag-)Angebote
const allDayOffers = computed(() => {
  const list = Array.isArray(reportData.value.allDayOffers) ? reportData.value.allDayOffers : [];
  return list.map(it => {
    const subject = it?.subject || it?.topic || 'Angebot';
    const s = it?.starts_at ? new Date(it.starts_at) : null;
    const e = it?.ends_at ? new Date(it.ends_at) : null;

    const rangeText = (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()))
        ? `${dmyHiBerlinFromDate(s)} - ${dmyHiBerlinFromDate(e)}`
        : '';

    return {subject, rangeText};
  });
});

const dateDisplay = computed(() => {
  const v = props.date;
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return String(v);
  }
  return new Intl.DateTimeFormat('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(d);
});

const timelineRows = computed(() => {
  const tl = Array.isArray(reportData.value.timeline) ? reportData.value.timeline : [];
  const out = [];

  const reportDay = String(props.date || '').trim(); // NEU

  for (let i = 0; i < tl.length; i++) {
    const it = tl[i];
    const kind = String(it?.kind || '');

    if (kind === 'arrival') {
      const d = it?.time ? new Date(it.time) : null;

      // NEU: Wenn ein Aufenthalt über mehrere Tage dauert,
      // dann Ankunft nur am Tag anzeigen, an dem sie tatsächlich stattfindet.
      if (d && reportDay && berlinYmd(d) !== reportDay) {
        continue;
      }

      out.push({
        kind: 'arrival',
        timeText: d ? formatHHMM(d) : '',
        dotClass: 'dot-success',
        first: Boolean(it?.first),
        externalUploaded: Boolean(it?.externalUploaded),
        timelineIndex: i,
        canEditStay: true
      });
      continue;
    }

    if (kind === 'release') {
      const d = it?.time ? new Date(it.time) : null;

      // NEU: Wenn ein Aufenthalt über mehrere Tage dauert,
      // dann Entlassen nur am Tag anzeigen, an dem es tatsächlich stattfindet.
      if (d && reportDay && berlinYmd(d) !== reportDay) {
        continue;
      }

      out.push({
        kind: 'release',
        timeText: d ? formatHHMM(d) : '',
        dotClass: 'dot-release',
        reason: it?.reason || '',
        note: it?.note,
        externalUploaded: Boolean(it?.externalUploaded),
        timelineIndex: i,
        canEditStay: true
      });
      continue;
    }

    if (kind === 'event') {
      const start = it?.starts_at ? new Date(it.starts_at) : null;
      const end = it?.ends_at ? new Date(it.ends_at) : null;

      // NEU: Ganztägige Zwischentage nicht als Timeline-Row rendern (kommen in allDayOffers)
      if (start && end && isAllDayOfferForReportDay(start, end)) {
        continue;
      }

      const subject = it?.subject || it?.topic || 'Angebot';
      const mandatory = !!(it?.is_mandatory ?? it?.isMandatory);

      const multiDay = (start && end) ? isMultiDayOfferBerlin(start, end) : false;

      // NEU: Bei mehrtägig -> Start/Ende MIT Datum anzeigen
      const startTxt = start ? (multiDay ? dmyHiBerlinFromDate(start) : formatHHMM(start)) : '';
      const endTxt = end ? (multiDay ? dmyHiBerlinFromDate(end) : formatHHMM(end)) : '';
      const leftLabel = startTxt && endTxt ? `${subject} (${startTxt}-${endTxt})` : subject;

      const checkin = it?.checkin_at ? new Date(it.checkin_at) : null;
      const checkout = it?.checkout_at ? new Date(it.checkout_at) : null;

      // NEU: Attendance bei mehrtägig ebenfalls MIT Datum anzeigen
      const kommenText = checkin ? formatTimeForAttendance(checkin, multiDay) : '';
      const gehenText = checkout ? formatTimeForAttendance(checkout, multiDay) : '';
      const hasAnyTime = !!(kommenText || gehenText);

      const provided = Number(it?.lateness_seconds ?? it?.latenessSeconds);
      const sec = (mandatory && Number.isFinite(provided) && provided > 0) ? provided : 0;
      const fehlzeit = secondsToHHMM(sec);

      const dotClass = checkin
          ? (sec > 0 ? 'dot-danger' : 'dot-success')
          : (mandatory ? 'dot-danger' : '');
      const lateClass = fehlzeit && mandatory ? 'late-badge' : '';

      out.push({
        kind: 'event',
        leftLabel,
        subject,
        startTxt,
        endTxt,
        isMandatory: mandatory,
        kommenText,
        gehenText,
        hasAnyTime,
        fehlzeit,
        lateClass,
        dotClass,
        timelineIndex: i
      });
    }
  }

  return out;
});

// Stays aus der Timeline ableiten (arrival -> release, inkl. Attendances dazwischen)
const stays = computed(() => {
  const tl = Array.isArray(reportData.value.timeline) ? reportData.value.timeline : [];
  const result = [];
  let current = null;

  for (let i = 0; i < tl.length; i++) {
    const it = tl[i];
    const kind = String(it?.kind || '');

    if (kind === 'arrival') {
      // alten ggf. verwerfen, wenn kein Release gefunden wurde
      current = {
        id: it?.stayId ?? null,
        arrivalIndex: i,
        releaseIndex: null,
        arrivalTime: it?.time ? new Date(it.time) : null,
        releaseTime: null,
        attendances: []
      };
      continue;
    }

    if (kind === 'release' && current) {
      current.releaseIndex = i;
      current.note = it?.note;
      current.releaseTime = it?.time ? new Date(it.time) : null;
      result.push(current);
      current = null;
      continue;
    }

    // Events zwischen arrival und release als attendances merken
    if (kind === 'event' && current) {
      const checkin = it?.checkin_at ? new Date(it.checkin_at) : null;
      const checkout = it?.checkout_at ? new Date(it.checkout_at) : null;

      if (checkin || checkout) {
        current.attendances.push({
          checkin,
          checkout,
          eventId: it?.eventId ?? it?.event_id ?? null,
          attendanceId: it?.attendanceId ?? it?.attendance_id ?? null,
          subject: it?.subject ?? null,
          topic: it?.topic ?? null
        });
      }
    }
  }

  // Falls der letzte Stay des Tages noch offen ist (keine Entlassung),
  // trotzdem als Stay übernehmen, damit der Bearbeiten-Dialog geöffnet werden kann.
  if (current) {
    result.push(current);
  }

  return result;
});

// Hilfsfunktion: Stay zu einer Timeline-Zeile finden (per arrival/release Index)
function findStayForTimelineIndex(idx) {
  const idxNum = Number(idx);
  if (!Number.isFinite(idxNum)) return null;
  return stays.value.find(stay =>
      stay.arrivalIndex === idxNum ||
      stay.releaseIndex === idxNum
  ) || null;
}

function onEditStayClick(row) {
  const stay = findStayForTimelineIndex(row.timelineIndex);
  if (!stay) return;

  // angrenzende Stays für Grenzen ermitteln
  const all = stays.value;
  const pos = all.indexOf(stay);
  const prevStay = pos > 0 ? all[pos - 1] : null;
  const nextStay = pos >= 0 && pos < all.length - 1 ? all[pos + 1] : null;

  // NEU: reasonId aus der Timeline (kind=release) übernehmen, damit Dropdown vorausgewählt ist
  const tl = Array.isArray(reportData.value?.timeline) ? reportData.value.timeline : [];
  const relRow = Number.isFinite(stay.releaseIndex) ? tl[stay.releaseIndex] : null;

  const ridRaw = relRow?.reasonId ?? relRow?.reason_id ?? null;
  const ridNum = Number(ridRaw);
  const releaseReasonId = Number.isFinite(ridNum) && ridNum > 0 ? ridNum : null;

  stayEditState.value = {
    open: true,
    stay: {
      ...stay,
      prevStay,
      nextStay,
      releaseReasonId
    }
  };
}

// Callback nach erfolgreichem Speichern
async function handleStaySaved() {
  stayEditState.value.open = false;
  // Nach Speichern Tagesbericht neu laden, damit Zeiten aktualisiert werden
  await loadDailyReport();
  // Zusätzlich: gruppierte Tagesübersicht neu laden (zur Aktualisierung externer Timelines)
  try {
    await eventsApi.getDayGrouped({date: props.date, groupBy: 'student'});
  } catch (e) {
    // still ignorieren – UI bleibt funktionsfähig
  }
  // Eltern-Komponente informieren, damit z.B. studentPresence-Lanes neu berechnet werden
  emit('saved');
}

const totalLateHHMM = computed(() => {
  const secs = Number(reportData.value?.totalLateSeconds || 0);
  return secondsToHHMM(secs);
});
</script>

<style scoped>
.timeline {
  display: grid;
  gap: 10px;
  position: relative; /* Overlay (.now-layer) relativ zu Timeline */
}

/* Icon-Button für Stay-Edit rechts neben Uhrzeit */
.icon-btn {
  height: 30px;
  min-width: 30px;
  padding: 0;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.icon-btn .icon-edit {
  color: var(--muted);
}

.icon-btn:hover .icon-edit {
  color: var(--primary);
}

/* Icon für "extern übermittelt" hinter der Zeit */
.upload-icon {
  margin-left: 6px;
  font-size: 14px;
  color: var(--muted);
  vertical-align: middle;
}

/* Neue zweispaltige Zeilen für Tagesbericht */
.pair-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid var(--border);
}

.pair-left {
  font-size: 14px;
  line-height: 20px;
}

.pair-left.underline {
  text-decoration: underline;
}

.pair-right {
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  white-space: nowrap;
}

/* Dezente Meta-Zeilen oben/unten (Ankunft/Entlassen) */
.tl-special {
  font-size: 14px;
  line-height: 20px;
  color: var(--text);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0 6px 0;
  border-bottom: 1px solid var(--border);
}

/* Timeline-Zeile (Alt; verbleibt für Rückwärtskompatibilität) */
.tl-row {
  display: grid;
  grid-template-columns: 100px 24px 1fr;
  align-items: start;
  gap: 8px;
  min-height: 32px;
}

.tl-time {
  font-size: 12px;
  color: var(--muted);
  line-height: 20px;
}

/* Vertikale Linie mit Punkt (Alt) */
.tl-line {
  position: relative;
  height: 100%;
  min-height: 24px;
}

.tl-dot {
  position: absolute;
  left: 50%;
  transform: translate(-50%, 5px);
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: 2px solid var(--border-strong); /* Grundfarbe: grau */
  background: #fff; /* Grund-BG: weiß */
  z-index: 2;
}

.tl-rail {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: -10px;
  bottom: -10px;
  width: 2px;
  background: var(--border-strong);
  z-index: 1;
}

/* Caret (kleiner Pfeil) für Ankunft/Entlassen */
.tl-caret {
  position: absolute;
  left: 50%;
  transform: translate(-40%, 5px);
  width: 0;
  height: 0;
  z-index: 2;
}

.tl-caret.caret-in {
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 10px solid var(--success);
}

.tl-caret.caret-out {
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 10px solid var(--warn);
}

/* Dot-Varianten (inkl. Status) */
.dot-in {
  border-color: var(--success);
}

.dot-out {
  border-color: var(--primary);
}

.dot-release {
  border-color: var(--warn);
  background: rgb(245, 158, 11);
}

.dot-success {
  border-color: var(--success);
  background: var(--success);
}

.dot-danger {
  border-color: var(--danger);
}

.dot-danger-fill {
  border-color: var(--danger);
  background: var(--danger);
}

/* Content (Alt) */
.tl-content {
  display: grid;
  gap: 2px;
}

.tl-title {
  font-size: 14px;
  line-height: 20px;
}

.tl-meta {
  font-size: 12px;
  line-height: 16px;
}

.tl-meta.late {
  color: #fff;
  background: var(--danger);
  border: 1px solid var(--danger);
  border-radius: 999px;
  display: inline-block;
  padding: 1px 6px;
}

/* Neue kompakte Fehlzeit-Badge rechts */
.late-badge {
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #fff;
  background: var(--danger);
  border: 1px solid var(--danger);
  border-radius: 999px;
  display: inline-block;
  padding: 1px 8px;
  margin-left: 6px;
  vertical-align: middle;
}

/* Transparente Variante für freiwillige Angebote (nur Kontur/Text in danger) */
.late-badge.soft {
  background: transparent;
  color: var(--danger);
  border-color: var(--danger);
}

/* Overlay-Schicht, die die Grid-Spalten spiegelt; Pfeil sitzt in Spalte 2 */
.now-layer {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 24px 1fr;
  pointer-events: none;
  z-index: 4;
}

.now-layer .now-col {
  grid-column: 2;
  position: relative;
}

.now-layer .now-arrow {
  position: absolute;
  left: -12px; /* von links in die Rail zeigen */
  /* top wird inline über nowTopPct gesetzt */
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 8px solid var(--primary);
}

/* Jetzt-Markierung: Pfeil zeigt von links auf die tl-line
.tl-row-rail.is-now .tl-line::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 5px;
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 8px solid var(--primary);
  z-index: 3;
  pointer-events: none;
}
@media (prefers-reduced-motion: no-preference) {
  .tl-row-rail.is-now .tl-line::before {
    animation: nowPulse 1.8s ease-in-out infinite;
  }

}
*/

@keyframes nowPulse {
  0% {
    opacity: .85;
    filter: brightness(1);
  }
  50% {
    opacity: 1;
    filter: brightness(1.1);
  }
  100% {
    opacity: .85;
    filter: brightness(1);
  }
}

/* Summary */
.tl-summary {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.late-total {
  font-weight: 600;
  padding: 2px 8px;
}

.muted {
  color: var(--muted);
}

/* Timeline-Layout (Mittelachse) */
.tl-row-rail {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 24px 1fr;
  align-items: start;
  gap: 8px;
  min-height: 32px;
  padding: 4px 0;
}

.tl-row-rail .release-note {
  display: block;
  font-size: 11px;
  line-height: 12px;
  padding-left: 70px;
  font-weight: 300;
}

.tl-left {
  font-size: 14px;
  line-height: 20px;
}

.tl-left .underline,
.tl-left.underline {
  text-decoration: underline;
}

.tl-left em {
  font-style: italic;
}

.tl-right {
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  position: relative;
}

.tl-right .icon-btn {
  width: 22px;
  height: 22px;
  top: -1px;
  position: absolute;
  min-width: auto;
  margin-left: 10px;
}

/* Warnhinterlegung, wenn eine akzeptierte Meldung existiert */
.warn-row {
  background: color-mix(in srgb, var(--warn) 10%, transparent);
  border-radius: 6px;
}

/* Inhalt rechts: „Meldung vorhanden“ + Info-Icon */
.accept-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: break-spaces;
}

.info-icon {
  color: var(--info);
  flex: 0 0 auto;
}

/* Warnhinterlegung für gesamte Zeile, wenn akzeptierte Meldung vorhanden ist */
.warn-row {
  background: color-mix(in srgb, var(--warn) 10%, transparent);
  border-radius: 6px;
}

/* Banner, das über alle drei Spalten läuft und die Standardinhalte ersetzt */
.tl-accept-banner {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: color-mix(in srgb, var(--warn) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--warn) 40%, transparent);
  border-radius: 6px;
  color: var(--text);
}

.tl-accept-banner .info-icon {
  color: var(--warn);
  flex: 0 0 auto;
}

.dialog-footer {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

/* NEU: Danger-Button-Variante auf bestehenden Tokens */
.btn-danger {
  background: var(--danger);
  color: #fff;
  border: 1px solid var(--danger);
}

.btn-danger:hover {
  background: color-mix(in srgb, var(--danger) 90%, #000);
}
</style>
